import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { useSchool } from '../hooks/useSchool';

const TicketForm = ({ onTicketsUpdated, pbisValues = [] }) => {
  const schoolId = useSchool(); // e.g. "nlcs"

  const [students, setStudents] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [valueType, setValueType] = useState('');
  const [note, setNote] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [staffSearch, setStaffSearch] = useState('');
  const [studentFocused, setStudentFocused] = useState(false);
  const [staffFocused, setStaffFocused] = useState(false);
  const [tickets, setTickets] = useState([]);

  const fetchTickets = async () => {
    if (!schoolId) return;
    const ticketPath = collection(db, 'schools', schoolId, 'tickets');
    const ticketsSnap = await getDocs(ticketPath);
    const ticketList = ticketsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setTickets(ticketList);
  };

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!schoolId) return;
      const uq = query(collection(db, 'users'), where('schoolId', '==', schoolId));
      const snapshot = await getDocs(uq);
      const all = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setStudents(all.filter((u) => u.role === 'student'));
      setStaffList(all.filter((u) => u.role === 'staff' || u.role === 'admin'));
    };
    fetchUsers();
  }, [schoolId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!schoolId) {
      alert('No school selected. Make sure you are on your school URL.');
      return;
    }
    if (!selectedStudent || !selectedStaff || !valueType || quantity < 1) return;

    setSubmitting(true);
    const timestamp = Timestamp.now();
    const promises = [];

    for (let i = 0; i < quantity; i++) {
      const ticketData = {
        schoolId, // 🔒 scope to school
        studentId: selectedStudent.id,
        valueType,
        note,
        quantity: 1, // keep your per-ticket quantity 1 pattern
        givenBy: {
          uid: selectedStaff.id,
          name: selectedStaff.name || '',
          email: selectedStaff.email || '',
        },
        timestamp, // normalized field name
        createdBy: auth.currentUser?.uid || null,
      };
      // ✅ New:
const ticketPath = collection(db, 'schools', schoolId, 'tickets');
promises.push(addDoc(ticketPath, ticketData));
    }

    try {
      await Promise.all(promises);
      await fetchTickets();

      setValueType('');
      setNote('');
      setQuantity(1);

      if (onTicketsUpdated) onTicketsUpdated();

      setSelectedStudent((prev) =>
        prev ? { ...prev, ticketBalance: (prev.ticketBalance || 0) + quantity } : prev
      );

      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      console.error('Error adding ticket:', err);
      alert(`❌ Error adding ticket: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setSelectedStudent(null);
    setSelectedStaff(null);
    setStudentSearch('');
    setStaffSearch('');
    setValueType('');
    setNote('');
    setQuantity(1);
  };

  const filteredStudents = students.filter((s) =>
    (s.name || '').toLowerCase().includes(studentSearch.toLowerCase())
  );

  const filteredStaff = staffList.filter((s) =>
    (s.name || '').toLowerCase().includes(staffSearch.toLowerCase())
  );

  const getTicketBalance = (studentId) => {
    return tickets
      .filter((t) => t.studentId === studentId)
      .reduce((sum, t) => sum + (t.quantity ?? 1), 0);
  };

  return (
    <form onSubmit={handleSubmit} className="section-card" style={{ marginTop: '2rem' }}>
      <h2 className="section-title">Give a Ticket</h2>

      {!schoolId && (
        <div
          style={{
            background: '#fff7ed',
            border: '1px solid #fdba74',
            color: '#9a3412',
            padding: '0.5rem 0.75rem',
            borderRadius: 8,
            marginBottom: '1rem',
          }}
        >
          ⚠ Make sure you’re on your school URL (e.g. <code>/nlcs</code>) to load students and tickets.
        </div>
      )}

      {/* Student Search */}
      <div style={{ marginBottom: '1rem' }}>
        <label>Student:</label>
        <input
          type="text"
          placeholder="Search student..."
          value={studentSearch}
          onFocus={() => setStudentFocused(true)}
          onBlur={() => setTimeout(() => setStudentFocused(false), 150)}
          onChange={(e) => setStudentSearch(e.target.value)}
          style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem' }}
        />
        {studentFocused && studentSearch && (
          <div style={{ maxHeight: '150px', overflowY: 'auto', background: '#f9f9f9', borderRadius: '6px' }}>
            {filteredStudents.length === 0 ? (
              <div style={{ padding: '0.5rem' }}>No matches</div>
            ) : (
              filteredStudents.map((student) => (
                <div
                  key={student.id}
                  onClick={() => {
                    setSelectedStudent(student);
                    setStudentSearch(student.name);
                  }}
                  style={{
                    padding: '0.5rem',
                    cursor: 'pointer',
                    background: selectedStudent?.id === student.id ? '#e0f2fe' : '',
                  }}
                >
                  {student.name}
                </div>
              ))
            )}
          </div>
        )}
        {selectedStudent && (
          <div style={{ fontSize: '0.9rem', color: '#555' }}>
            🎫 Current Balance: <strong>{getTicketBalance(selectedStudent.id)}</strong>
          </div>
        )}
      </div>

      {/* Staff Search */}
      <div style={{ marginBottom: '1rem' }}>
        <label>Given By (Staff):</label>
        <input
          type="text"
          placeholder="Search staff..."
          value={staffSearch}
          onFocus={() => setStaffFocused(true)}
          onBlur={() => setTimeout(() => setStaffFocused(false), 150)}
          onChange={(e) => setStaffSearch(e.target.value)}
          style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem' }}
        />
        {staffFocused && staffSearch && (
          <div style={{ maxHeight: '150px', overflowY: 'auto', background: '#f9f9f9', borderRadius: '6px' }}>
            {filteredStaff.length === 0 ? (
              <div style={{ padding: '0.5rem' }}>No matches</div>
            ) : (
              filteredStaff.map((staff) => (
                <div
                  key={staff.id}
                  onClick={() => {
                    setSelectedStaff(staff);
                    setStaffSearch(staff.name);
                  }}
                  style={{
                    padding: '0.5rem',
                    cursor: 'pointer',
                    background: selectedStaff?.id === staff.id ? '#e0f7dc' : '',
                  }}
                >
                  {staff.name}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Quantity */}
      <div style={{ marginBottom: '1rem' }}>
        <label>Quantity:</label>
        <input
          type="number"
          min="1"
          value={quantity}
          onChange={(e) => setQuantity(parseInt(e.target.value, 10) || 1)}
          style={{ width: '100%', padding: '0.5rem' }}
        />
      </div>

      {/* Value Type */}
      <div style={{ marginBottom: '1rem' }}>
        <label>Value Type:</label>
        <select
  value={valueType}
  onChange={(e) => setValueType(e.target.value)}
  required
  style={{ width: '100%', padding: '0.5rem' }}
>
  <option value="">-- Select Value --</option>
  {pbisValues.map((val) => (
    <option key={val.id} value={val.name}>{val.name}</option>
  ))}
</select>
      </div>

      {/* Note */}
      <div style={{ marginBottom: '1rem' }}>
        <label>Optional Note:</label>
        <textarea
          rows="2"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="(optional)"
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
        <button type="submit" className="button-plus" disabled={submitting || !schoolId}>
          {submitting ? 'Submitting...' : `Submit ${quantity > 1 ? `${quantity} Tickets` : 'Ticket'}`}
        </button>
        <button type="button" className="button-minus" onClick={handleReset}>
          Reset
        </button>
      </div>

      {success && <p style={{ color: 'green', marginTop: '0.5rem' }}>✅ Ticket submitted!</p>}
    </form>
  );
};

export default TicketForm;